import { useEffect } from 'react'

import _ from 'lodash'
import { Controller, useFormContext } from 'react-hook-form'

import { AddressFields } from '@/features/user/additional-information/AddressFields/index'
import { PhoneInput } from '@/features/user/additional-information/PhoneInput'
import { cn } from '@/lib/utils'
import { useScrollThumb } from '@/shared/hooks/useScrollThumb'
import {
  CheckboxField,
  DateField,
  DateInput,
  DocumentField,
  Field,
  FormControl,
  SelectField,
  TextareaField,
  TextField
} from '@/shared/ui/Form'
import getRules from '@/shared/ui/Form/lib/rules'
import { ScrollThumb, scrollThumbStyles } from '@/shared/ui/ScrollThumb'

/**
 * Grupo de múltipla escolha.
 *
 * Não há campo de grupo de caixas na camada compartilhada, só `CheckboxField`
 * de caixa única. Composição local: uma `Controller` dona do array `values` e N
 * `CheckboxField` em modo controlado, que não tocam no `react-hook-form`
 * sozinhos.
 */
const MultipleChoiceChecklist = ({ name, options, required }) => {
  const { control } = useFormContext()
  // Mesma barra própria do `SelectContent`: nativa some fora do gesto no
  // touch e não segue a cor do evento.
  const { ref: scrollRef, thumbRef, visible, onScroll } = useScrollThumb(options)

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      rules={getRules({ required })}
      render={({ field }) => (
        // Limite de altura próprio, como todo outro widget de lista do
        // projeto (`SelectContent`, `AppMenuScrollArea`): sem isso, uma lista
        // de opções longa (ex: unidades CIEE) empurra o modal inteiro para
        // baixo do limite da tela, e o participante não enxerga nem alcança
        // as opções finais — ver bug form-select-long-list-not-scrolling.
        // `overflow-hidden`: sem isso, o `scrollHeight` da caixa rolável
        // (que fica visualmente contida em `max-h-72`) "vaza" para o
        // `AppMenuScrollArea` do modal por fora — o modal calculava haver
        // ~1900px de conteúdo quando na verdade só ~300px são visíveis,
        // abrindo um vão em branco enorme antes do rodapé. Confirmado
        // isolando a causa via medição de `scrollHeight` em cada ancestral.
        //
        // Mesmo `rounded` da caixa interna: sem isso o recorte do
        // `overflow-hidden` é um retângulo reto, e a trilha (também reta)
        // ultrapassa a curva do canto arredondado da borda.
        <div className="relative overflow-hidden rounded-[var(--control-r-md)]">
          <div
            ref={scrollRef}
            role="group"
            onScroll={onScroll}
            className={cn(
              'flex max-h-72 flex-col gap-1 overflow-y-auto overscroll-contain rounded-[var(--control-r-md)] border-2 border-contour bg-white px-3 py-2 pr-4 [-webkit-overflow-scrolling:touch]',
              scrollThumbStyles.hideNativeScrollbar
            )}
          >
            {options.map(option => {
              const checked = _.isArray(field.value)
                ? field.value.includes(option.value)
                : false

              return (
                <CheckboxField
                  key={option.value}
                  label={option.label}
                  value={checked}
                  onChange={() => {
                    const current = _.isArray(field.value) ? field.value : []
                    const next = checked
                      ? current.filter(value => value !== option.value)
                      : [...current, option.value]
                    field.onChange(next)
                  }}
                />
              )
            })}
          </div>
          <ScrollThumb visible={visible} thumbRef={thumbRef} />
        </div>
      )}
    />
  )
}

export const DigitalProductCheckoutAditionalInformationFields = ({
  id,
  namePrefix,
  name,
  answer,
  meta
}) => {
  const { setValue } = useFormContext()
  const kind = _.get(meta, 'kind')
  const options = _.get(meta, 'options', [])
  const required = options.includes('is_required')
  const hasInstructions = options.includes('has_instructions')
  const instructions = _.get(meta, 'instructions')
  const dateFormat = _.get(meta, 'date_format')
  const registrationNumberFormat = _.get(meta, 'registration_number_format')
  const termContent = _.get(meta, 'term_content')
  const textFormat = _.get(meta, 'text_format')
  const multipleChoiceIsMultiple = _.get(meta, 'multiple_choice_is_multiple')
  const multipleChoiceList = _.uniqBy(
    _.get(meta, 'multiple_choice_list', []),
    'name'
  )
  let slug = `${namePrefix}.value`
  if (kind == 'multiple_choice' && multipleChoiceIsMultiple)
    slug = `${namePrefix}.values`
  else if (kind == 'address') slug = `${namePrefix}.values_object`

  const rules = getRules({ required })

  useEffect(() => {
    setValue(`${namePrefix}.id`, id)
    setValue(`${namePrefix}.name`, name)
    setValue(`${namePrefix}.kind`, kind)
    setValue(`${namePrefix}.required`, required)

    if (answer) setValue(`${namePrefix}.value`, answer)

    if (kind == 'address')
      setValue(`${namePrefix}.values_object`, answer)
    else if (kind == 'registration_number')
      setValue(
        `${namePrefix}.registration_number_format`,
        registrationNumberFormat
      )
    else if (kind == 'multiple_choice')
      setValue(
        `${namePrefix}.multiple_choice_is_multiple`,
        multipleChoiceIsMultiple
      )
  }, [id, name, kind, required, answer])

  const requiredLabel = (
    <span
      className={
        required ? "after:content-['*'] after:ml-0.5 after:text-danger-text" : ''
      }
    >
      {name}
    </span>
  )

  return (
    <>
      <FormControl name={`${namePrefix}.id`} value={id} hidden />
      <FormControl name={`${namePrefix}.name`} value={name} hidden />
      <FormControl name={`${namePrefix}.kind`} value={kind} hidden />

      <Field name={slug} className="mb-6">
        <Field.Label>{requiredLabel}</Field.Label>

        {kind == 'term' && (
          // Mesma borda e raio dos demais campos deste formulário.
          <div className="block h-[150px] w-full overflow-y-auto rounded-[var(--control-r-md)] border-2 border-contour bg-white px-4 py-3 text-sm text-gray-700 mb-2">
            {termContent}
          </div>
        )}

        {kind == 'address' ? (
          <AddressFields
            namePrefix={slug}
            required={required}
            hideFieldsUntilZipCode
          />
        ) : (
          <Field.Control>
            {kind == 'phone' ? (
              // Telefone BR em dígitos puros: fixo tem 10, celular 11. Usa
              // `minLength`/`maxLength` porque `getRules` descarta o `pattern`
              // recebido — sem isso "119" passa como telefone válido.
              <PhoneInput
                name={slug}
                required={required}
                minLength={10}
                maxLength={11}
              />
            ) : kind == 'date' && dateFormat == 'only_date' ? (
              <DateField name={slug} rules={rules} />
            ) : kind == 'date' && dateFormat != 'only_date' ? (
              <DateInput
                name={slug}
                withTime={dateFormat == 'date_and_hour'}
                onlyTime={dateFormat == 'only_hour'}
                required={required}
              />
            ) : kind == 'registration_number' ? (
              <DocumentField
                name={slug}
                format={registrationNumberFormat == 'only_cpf' ? 'cpf' : 'cnpj'}
                rules={{
                  ...rules,
                  // `getRules({ pattern })` descarta o regex recebido, então
                  // `required` sozinho aceitaria qualquer valor não vazio,
                  // inclusive um CPF de dois dígitos.
                  validate: value => {
                    if (!required && !value) return true
                    const digitCount = String(value ?? '').replace(/\D/g, '').length
                    const isCpf = registrationNumberFormat == 'only_cpf'
                    if (digitCount === (isCpf ? 11 : 14)) return true
                    return isCpf ? 'CPF incompleto' : 'CNPJ incompleto'
                  }
                }}
              />
            ) : kind == 'rg' ? (
              <TextField name={slug} rules={rules} />
            ) : kind == 'term' ? (
              // O `div` é proposital: `CheckboxField` já renderiza um `<label>`
              // com o texto do aceite como nome acessível. Como filho direto do
              // `Field.Control`, o `Slot` injetaria o `id` do `Field` no mesmo
              // botão, resultando em dois rótulos. O `div` absorve o
              // `id`/`aria-*` sem entrar na cadeia de associação.
              <div>
                <CheckboxField
                  label="Eu declaro que li e aceito os termos acima descritos"
                  rules={rules}
                />
              </div>
            ) : kind == 'text' ? (
              textFormat == 'long' ? (
                <TextareaField name={slug} rules={rules} />
              ) : (
                <TextField name={slug} rules={rules} />
              )
            ) : kind == 'multiple_choice' && multipleChoiceIsMultiple ? (
              <MultipleChoiceChecklist
                name={slug}
                options={multipleChoiceList.map(item => ({
                  label: item.name,
                  value: item.name
                }))}
                required={required}
              />
            ) : kind == 'multiple_choice' && !multipleChoiceIsMultiple ? (
              // `SelectField` (Radix), não `<select>` nativo: a lista do
              // nativo é desenhada pelo sistema operacional, fora da árvore da
              // página, e ancorada por coordenadas que ignoram o contexto do
              // diálogo — dentro do modal ela abre deslocada para fora. Não há
              // CSS que resolva; a lista não pertence à página.
              <SelectField
                name={slug}
                options={multipleChoiceList.map(item => ({
                  label: item.name,
                  value: item.name
                }))}
                rules={rules}
                isClearable={!required}
              />
            ) : null}
          </Field.Control>
        )}

        {hasInstructions && instructions && (
          <Field.Description>{instructions}</Field.Description>
        )}
        <Field.Error />
      </Field>
    </>
  )
}
